import { useState, type FormEvent } from "react";
import { Button } from "../../components/Button";
import { ErrorSummary } from "../../components/ErrorSummary";

interface Props {
  busy: boolean;
  online: boolean;
  onSubmit: (email: string, reminderOptIn: boolean) => void;
  onPrivacy?: () => void;
  /** Prefix all DOM ids when more than one save prompt is present. */
  idPrefix?: string;
  /** Identifies the prompt's placement for styling and anchor targeting. */
  placement?: "top" | "bottom";
}

export function EmailCapture({ busy, online, onSubmit, onPrivacy, idPrefix = "email", placement = "bottom" }: Props) {
  const legacyDefaultIds = idPrefix === "email";
  const ids = {
    title: `${idPrefix}-capture-title`,
    email: legacyDefaultIds ? "email" : `${idPrefix}-email`,
    error: legacyDefaultIds ? "email-error" : `${idPrefix}-email-error`,
    privacy: legacyDefaultIds ? "email-privacy" : `${idPrefix}-email-privacy`,
  };
  const emailLabel = placement === "top" ? "Email for your secure link" : "Email address";
  const submitLabel = placement === "top" ? "Save plan securely" : "Email me a secure link";
  const [email, setEmail] = useState("");
  const [reminderOptIn, setReminderOptIn] = useState(false);
  const [error, setError] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      setError("Enter an email address in the correct format, such as name@example.com.");
      return;
    }
    setError("");
    onSubmit(email.trim(), reminderOptIn);
  };
  return (
    <section id={`${idPrefix}-save-plan`} className={`email-card email-card--${placement}`} aria-labelledby={ids.title}>
      <div>
        <p className="eyebrow">Optional</p>
        <h2 id={ids.title}>Save this plan</h2>
        <p>Enter your email to receive a secure link back to this estimate. Reminders are a separate choice. You can turn them off, or request deletion, from your saved plan at any time.</p>
      </div>
      <form onSubmit={submit} noValidate>
        <ErrorSummary errors={error ? { email: error } : {}} fieldIds={{ email: ids.email }} />
        <div className={`field ${error ? "field--error" : ""}`}>
          <label htmlFor={ids.email}>{emailLabel}</label>
          {error && <p className="field-error" id={ids.error}><span className="sr-only">Error: </span>{error}</p>}
          <input id={ids.email} type="email" autoComplete="email" value={email} aria-invalid={Boolean(error)} aria-describedby={error ? `${ids.error} ${ids.privacy}` : ids.privacy} onChange={(event) => setEmail(event.target.value)} />
        </div>
        <label className="check-choice">
          <input type="checkbox" checked={reminderOptIn} onChange={(event) => setReminderOptIn(event.target.checked)} />
          <span><strong>Email me reminders about this plan</strong>{" "}<small>Unchecked by default. Saving the plan doesn’t need reminders.</small></span>
        </label>
        <p id={ids.privacy} className="field-hint">We use this address only to save your plan and deliver the options you choose. <a href="/privacy" onClick={onPrivacy ? (event) => { event.preventDefault(); onPrivacy(); } : undefined}>See privacy details.</a></p>
        <Button type="submit" busy={busy} disabled={!online}>{busy ? "Sending secure link…" : submitLabel}</Button>
      </form>
    </section>
  );
}
